// İSG Portal — belge türü kaydı çözümleyicisi (SAF, React yok; mobil-güvenli).
// Anahtar her zaman `(entity, code)`: aynı kod iki entity'de bulunabilir (`Insurance`).

import type { DocumentEntity, DocumentTypeDto, DocumentTypeRef } from '../api/documentTypes';

type Types = readonly DocumentTypeDto[] | undefined;

/** Kayıt sırası (§27.0(5)): personel, araç, firma — tüm entity'leri sunan seçiciler ve süzgeçler. */
export const DOCUMENT_ENTITIES: readonly DocumentEntity[] = ['personnel', 'vehicle', 'company'];

/**
 * `DocumentTypeRef` taşıyan yüzeylerin (§22 gereksinimler, §31.2 routing, §31.5 sorumlu belgeler)
 * entity'leri, §27.0(5) sırasıyla: önce personel, sonra araç. `company` bu yüzeylerde yoktur (#19).
 */
export const REQUIREMENT_ENTITIES: readonly DocumentTypeRef['entity'][] = ['personnel', 'vehicle'];

/** Satır/seçim anahtarı `entity:code` — aynı kod iki entity'de bulunabilir (`Insurance`). */
export function documentTypeRefKey(ref: DocumentTypeRef): string {
  return `${ref.entity}:${ref.code}`;
}

const ordinal = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Birebir (büyük/küçük harf duyarlı, ordinal) arama. */
export function findDocumentType(
  types: Types,
  entity: DocumentEntity,
  code: string,
): DocumentTypeDto | undefined {
  return types?.find((t) => t.entity === entity && t.code === code);
}

/**
 * Etiket: dil öneki eşleşmesi → `labelXx` → `labelTr` → ham `code`. Asla boş dönmez; kayıt
 * yüklenmemişse/tür bilinmiyorsa ham kod görünür (etiketler ekranı engellemez).
 */
export function documentTypeLabel(
  types: Types,
  entity: DocumentEntity,
  code: string,
  lang: string,
): string {
  const type = findDocumentType(types, entity, code);
  if (!type) return code;
  // `i18n.language` can be undefined before init / on a detector miss: fall through to labelTr.
  const l = ((lang as string | undefined) ?? '').toLowerCase();
  const byLang = l.startsWith('de')
    ? type.labelDe
    : l.startsWith('en')
      ? type.labelEn
      : type.labelTr;
  return byLang.trim() || type.labelTr.trim() || code;
}

/** Seçicide sunulacak türler: yalnız aktif; `sortOrder` artan, eşitlikte `code` ordinal. */
export function selectableTypes(types: Types, entity: DocumentEntity): DocumentTypeDto[] {
  return (types ?? [])
    .filter((t) => t.entity === entity && t.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || ordinal(a.code, b.code));
}

/**
 * Bir gereksinim/kalem kodu artık kullanılamıyor mu: kayıt YÜKLENMİŞ ve tür ya kayıtta yok ya da
 * pasif (§27.0(6) — böyle bir gereksinim hiçbir zaman karşılanmaz, yükleme `INACTIVE`/`INVALID`
 * alır). Kayıt henüz yüklenmemişse `false` — etiketler gibi bu da ekranı engellemez.
 */
export function isInactiveOrUnknownDocumentType(
  types: Types,
  entity: DocumentEntity,
  code: string,
): boolean {
  return types !== undefined && findDocumentType(types, entity, code)?.isActive !== true;
}

/** `EndDateRequired`: bitiş tarihi zorunlu (yıldız). */
export function requiresValidUntil(type: DocumentTypeDto | undefined): boolean {
  return type?.expiryRule === 'EndDateRequired';
}

/** Bitiş tarihi alanı görünür mü: tür seçilmiş ve kural `NoExpiry` değil. */
export function showsValidUntil(type: DocumentTypeDto | undefined): boolean {
  return type !== undefined && type.expiryRule !== 'NoExpiry';
}
